import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Radio,
  Checkbox,
  Slider,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import http from '../http/http';
import { getHashObj } from '../getSubjectId';
import { usePromStatus, refreshPromStatusExternally } from './usePromStatus';
import {
  QuestionnaireContainer,
  ProgressContainer,
  ProgressFill,
  ProgressBarWrapper,
  TimeEstimate,
  QuestionBox,
  QuestionText,
  RequiredStar,
  OptionsContainer,
  PillOption,
  CheckboxOption,
  CheckboxOptionActive,
  NrsGrid,
  NrsCell,
  NrsCellActive,
  NrsLabel,
  BsfsRow,
  BsfsLabel,
  RecallHint,

  ThankYouBox,
  StyledDialog,
  StyledDialogTitle,
  ReferencesSection,
  ReferencesTitle,
  ReferenceItem,
  ReferenceTitle,
  ReferenceLink,
} from './PromQuestionnaire.styled';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BsfsLabelItem {
  type: string;
  desc: string;
}

interface ScaleLabelItem {
  en: string;
  de: string;
}

interface OptionItem {
  label: { en: string; de: string };
}

interface Question {
  id: string;
  section: string;
  question: { en: string; de: string };
  responseType: 'single' | 'multiple' | 'number' | 'nrs' | 'scale' | 'bsfs';
  required?: boolean;
  options?: OptionItem[];
  scaleLabels?: ScaleLabelItem[];
  bsfsLabels?: BsfsLabelItem[];
  min?: number;
  max?: number;
  step?: number;
}

interface SectionDef {
  key: string;
  label: { en: string; de: string };
  questions: Question[];
  recallHint?: { en: string; de: string };
}

interface DraftData {
  currentSection: number;
  answers: Record<string, unknown>;
}

interface CompletionResult {
  who5Score?: number;
  phq2Score?: number;
  gad2Score?: number;
  overallScore?: number;
  message?: { en: string; de: string };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SECTIONS_ORDER = [
  'demographics',
  'lifestyle',
  'who5',
  'mood',
  'energy_sleep',
  'body_stress',
  'digestive',
] as const;

const STORAGE_KEY = 'prom_draft';
const COMPLETED_KEY = 'prom_completed';

const STRESS_DEFINITION: { en: string; de: string } = {
  en: 'Stress is your body\'s reaction to pressure from a situation. Your body releases hormones like adrenaline and cortisol in response to a stressor.',
  de: 'Stress ist die Reaktion Ihres Körpers auf den Druck einer Situation. Ihr Körper setzt Hormone wie Adrenalin und Cortisol als Reaktion auf einen Stressor frei.',
};

// ─── References ────────────────────────────────────────────────────────────────

const REFERENCES = [
  {
    title: 'WHO-5',
    citations: [
      'Topp CW, Østergaard SD, Søndergaard S, Bech P. The WHO-5 Well-Being Index: a systematic review of the literature. Psychotherapy and Psychosomatics. 2015;84(3):167-176.',
      { text: 'Official site: psykiatri-regionh.dk/who-5/', url: 'psykiatri-regionh.dk/who-5/' },
    ],
  },
  {
    title: 'PHQ-2',
    citations: [
      'Kroenke K, Spitzer RL, Williams JB. The Patient Health Questionnaire-2: validity of a two-item depression screener. Medical Care. 2003;41(11):1284-1292.',
      'Available via Pfizer\'s Patient Health Questionnaire screeners library, public domain.',
    ],
  },
  {
    title: 'GAD-2',
    citations: [
      'Kroenke K, Spitzer RL, Williams JB, Monahan PO, Löwe B. Anxiety disorders in primary care: prevalence, impairment, comorbidity, and detection. Annals of Internal Medicine. 2007;146(5):317-325.',
      'Public domain.',
    ],
  },
  {
    title: 'Single-item fatigue NRS',
    citations: [
      'Butt Z, Lai JS, Rao D, Heinemann AW, Bill A, Cella D. Measurement of fatigue in cancer, stroke, and HIV using the Functional Assessment of Chronic Illness Therapy - Fatigue (FACIT-F) scale. Journal of Psychosomatic Research. 2013;74(1):64-68.',
      'Temel JS, Pirl WF, Recklitis CJ, Cashavelly B, Lynch TJ. Feasibility and validity of a one-item fatigue screen in a thoracic oncology clinic. Journal of Thoracic Oncology. 2006;1(5):454-459.',
    ],
  },
  {
    title: 'Single-item sleep quality',
    citations: [
      'Snyder E, Cai B, DeMuro C, Morrison MF, Ball W. A new single-item sleep quality scale: results of psychometric evaluation in patients with chronic primary insomnia and depression. Journal of Clinical Sleep Medicine. 2018;14(11):1849-1857.',
    ],
  },
  {
    title: 'Bristol Stool Form Scale',
    citations: [
      'Lewis SJ, Heaton KW. Stool form scale as a useful guide to intestinal transit time. Scandinavian Journal of Gastroenterology. 1997;32(9):920-924.',
    ],
  },
  {
    title: 'Single-item stress (Elo)',
    citations: [
      'Elo AL, Leppänen A, Jahkola A. Validity of a single-item measure of stress symptoms. Scandinavian Journal of Work, Environment & Health. 2003;29(6):444-451.',
    ],
  },
  {
    title: 'IPAQ',
    citations: [
      'Craig CL, Marshall AL, Sjöström M, et al. International Physical Activity Questionnaire: 12-country reliability and validity. Medicine and Science in Sports and Exercise. 2003;35(8):1381-1395.',
      { text: 'Official site: ipaq.ki.se', url: 'ipaq.ki.se' },
    ],
  },
];

// ─── BSFS SVG Icons ──────────────────────────────────────────────────────────

function BsfsIcon(type: number): React.ReactNode {
  const size = 48;
  switch (type) {
    case 1: // separate hard lumps
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={14} cy={16} r={5} fill="#8B6914" />
          <circle cx={28} cy={12} r={4} fill="#8B6914" />
          <circle cx={20} cy={28} r={5} fill="#8B6914" />
          <circle cx={34} cy={26} r={4} fill="#8B6914" />
        </svg>
      );
    case 2: // sausage-shaped but lumpy
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <rect x={6} y={14} width={36} height={20} rx={10} fill="#8B6914" />
          <circle cx={14} cy={18} r={3} fill="#A07820" />
          <circle cx={24} cy={20} r={3} fill="#A07820" />
          <circle cx={34} cy={18} r={3} fill="#A07820" />
        </svg>
      );
    case 3: // sausage with cracks
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <rect x={6} y={14} width={36} height={20} rx={10} fill="#8B6914" />
          <line x1={16} y1={14} x2={16} y2={34} stroke="#A07820" strokeWidth={1.5} />
          <line x1={24} y1={14} x2={24} y2={34} stroke="#A07820" strokeWidth={1.5} />
          <line x1={32} y1={14} x2={32} y2={34} stroke="#A07820" strokeWidth={1.5} />
        </svg>
      );
    case 4: // smooth and soft
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <rect x={6} y={14} width={36} height={20} rx={10} fill="#8B6914" />
        </svg>
      );
    case 5: // soft blobs with edges
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={18} cy={24} r={8} fill="#8B6914" />
          <circle cx={30} cy={24} r={8} fill="#8B6914" />
        </svg>
      );
    case 6: // fluffy pieces, mushy
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <path d="M6 24 Q12 16 18 24 Q24 32 30 24 Q36 16 42 24" stroke="#8B6914" strokeWidth={4} fill="none" strokeLinecap="round" />
        </svg>
      );
    case 7: // watery
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <ellipse cx={24} cy={24} rx={18} ry={10} fill="#C4A44A" opacity={0.5} />
        </svg>
      );
    default:
      return null;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLangText(item: { en: string; de: string } | undefined, lang: string): string {
  if (!item) return '';
  return item[lang as 'en' | 'de'] || item.en;
}

const SECTION_LABELS: Record<string, string> = {
  demographics: 'Demographics',
  lifestyle: 'Lifestyle',
  who5: 'WHO-5 Wellbeing',
  mood: 'Mood & Emotions',
  energy_sleep: 'Energy & Sleep',
  body_stress: 'Body & Stress',
  digestive: 'Digestive Health',
};

function getDefaultSectionLabel(key: string): string {
  return SECTION_LABELS[key] || key;
}

function getDraft(): DraftData | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function saveDraft(data: DraftData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function clearDraft() {
  localStorage.removeItem(STORAGE_KEY);
}

function markCompleted() {
  localStorage.setItem(COMPLETED_KEY, JSON.stringify({ timestamp: Date.now() }));
}



// ─── Styles (injected) ───────────────────────────────────────────────────────

// (Styled components are in PromQuestionnaire.styled.ts)

// ─── Sub-components ──────────────────────────────────────────────────────────

function PillSelector({
  options,
  value,
  onChange,
  lang,
  required,
}: {
  options: OptionItem[];
  value: string | undefined;
  onChange: (v: string) => void;
  lang: string;
  required?: boolean;
}) {
  return (
    <OptionsContainer>
      {options.map((opt, i) => {
        const active = value === String(i);
        return (
          <PillOption
            key={i}
            active={active}
            onClick={() => onChange(String(i))}
            tabIndex={0}
          >
            {getLangText(opt.label, lang)}
            {required && <RequiredStar>*</RequiredStar>}
          </PillOption>
        );
      })}
    </OptionsContainer>
  );
}

function MultipleSelector({
  options,
  values,
  onChange,
  lang,
  required,
}: {
  options: OptionItem[];
  values: number[];
  onChange: (v: number[]) => void;
  lang: string;
  required?: boolean;
}) {
  const toggle = (i: number) => {
    if (values.includes(i)) {
      onChange(values.filter((v) => v !== i));
    } else {
      onChange([...values, i]);
    }
  };

  return (
    <OptionsContainer>
      {options.map((opt, i) => {
        const active = values.includes(i);
        return (
          <CheckboxOption
            key={i}
            active={active}
            onClick={() => toggle(i)}
            tabIndex={0}
          >
            <CheckboxOptionActive checked={active}>
              <Checkbox
                checked={active}
                onChange={() => toggle(i)}
                size="small"
              />
            </CheckboxOptionActive>
            <Typography variant="body2">{getLangText(opt.label, lang)}</Typography>
            {required && <RequiredStar>*</RequiredStar>}
          </CheckboxOption>
        );
      })}
    </OptionsContainer>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <TextField
      type="number"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min, max, step, style: { padding: '8px 12px' } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
          '&:hover': { backgroundColor: '#eeeeee' },
        },
      }}
    />
  );
}

function NrsScale({
  value,
  onChange,
  scaleLabels,
  min,
  max,
  lang,
  required,
}: {
  value: number | null;
  onChange: (v: number) => void;
  scaleLabels?: ScaleLabelItem[];
  min?: number;
  max?: number;
  lang: string;
  required?: boolean;
}) {
  const rangeMin = min ?? 0;
  const rangeMax = max ?? 10;
  const range = rangeMax - rangeMin;
  const labels = scaleLabels
    ? [getLangText(scaleLabels[0], lang), getLangText(scaleLabels[scaleLabels.length - 1], lang)]
    : [String(rangeMin), String(rangeMax)];

  return (
    <Box>
      <NrsGrid>
        {Array.from({ length: range + 1 }, (_, i) => {
          const num = rangeMin + i;
          return (
            <NrsCell
              key={num}
              active={value === num}
              onClick={() => onChange(num)}
              tabIndex={0}
            >
              <NrsCellActive active={value === num}>
                {num}
              </NrsCellActive>
            </NrsCell>
          );
        })}
      </NrsGrid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <NrsLabel>{labels[0]}</NrsLabel>
        <NrsLabel>{labels[1]}</NrsLabel>
      </Box>
      {required && <RequiredStar sx={{ mt: 0.5, display: 'block' }}>*</RequiredStar>}
    </Box>
  );
}

function BsfsSelector({
  bsfsLabels,
  value,
  onChange,
  lang,
}: {
  bsfsLabels?: BsfsLabelItem[];
  value: number | null;
  onChange: (v: number) => void;
  lang: string;
}) {
  const labels = bsfsLabels || [];

  return (
    <Box>
      {labels.map((label, i) => {
        const active = value === i + 1;
        return (
          <BsfsRow
            key={i}
            active={active}
            onClick={() => onChange(i + 1)}
            tabIndex={0}
          >
            <Box sx={{ flexShrink: 0 }}>
              {BsfsIcon(i + 1)}
            </Box>
            <BsfsLabel active={active}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {`Type ${label.type}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {label.desc}
              </Typography>
            </BsfsLabel>
            <Radio
              checked={active}
              onChange={() => onChange(i + 1)}
              size="small"
              sx={{ ml: 1 }}
            />
          </BsfsRow>
        );
      })}
    </Box>
  );
}

function ScalePills({
  min,
  max,
  labels,
  value,
  onChange,
  lang,
  required,
}: {
  min: number;
  max: number;
  labels: ScaleLabelItem[];
  value: number | null;
  onChange: (v: number) => void;
  lang: string;
  required?: boolean;
}) {
  const range = max - min;

  return (
    <Box>
      <OptionsContainer>
        {Array.from({ length: range + 1 }, (_, i) => {
          const num = min + i;
          const active = value === num;
          return (
            <PillOption
              key={num}
              active={active}
              onClick={() => onChange(num)}
              tabIndex={0}
              style={{ maxWidth: 'none' }}
            >
              {getLangText(labels[i], lang)}
              {required && <RequiredStar>*</RequiredStar>}
            </PillOption>
          );
        })}
      </OptionsContainer>
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface PromQuestionnaireProps {
  open: boolean;
  onClose: () => void;
}

export function PromQuestionnaire({ open, onClose }: PromQuestionnaireProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const { loading: statusLoading } = usePromStatus();

  const [sections, setSections] = useState<SectionDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState<CompletionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [otherSymptomText, setOtherSymptomText] = useState('');
  const [supplementText, setSupplementText] = useState('');
  const [familyHistoryText, setFamilyHistoryText] = useState('');
  const [dietTypeText, setDietTypeText] = useState('');

  const prevOpenRef = useRef(open);

  // Fetch questions on popup open
  useEffect(() => {
    if (!open) return;

    const fetchQuestions = async () => {
      setLoading(true);
      setError(null);
      try {
        const allQuestions = await http.get('/api/prom/questions', { raw: true });
        const grouped: Record<string, Question[]> = allQuestions?.data || allQuestions || {};

        const sectionDefs: SectionDef[] = SECTIONS_ORDER.map((key) => {
          return {
            key,
            label: { en: key, de: key },
            questions: (grouped[key] || []).map((q: any) => {
              const normalized: any = { ...q, id: q.questionKey || q.id };
              if (q.scaleLabels && typeof q.scaleLabels === 'object' && !Array.isArray(q.scaleLabels)) {
                const labels = q.scaleLabels;
                const maxIdx = Math.max((labels.en || []).length, (labels.de || []).length) - 1;
                normalized.scaleLabels = Array.from({ length: maxIdx + 1 }, (_, i) => ({
                  en: labels.en?.[i] || '',
                  de: labels.de?.[i] || '',
                }));
              }
              if (q.bsfsLabels && typeof q.bsfsLabels === 'object' && !Array.isArray(q.bsfsLabels)) {
                const labels = q.bsfsLabels;
                if (Array.isArray(labels.en)) {
                  const first = labels.en[0];
                  if (first && typeof first === 'object' && 'type' in first) {
                    normalized.bsfsLabels = labels.en.map((item: { type: string; desc: string }) => ({
                      type: item.type,
                      desc: item.desc,
                    }));
                  } else {
                    normalized.bsfsLabels = labels.en.map((en: string, i: number) => ({
                      type: en,
                      desc: (labels.de || [])[i] || '',
                    }));
                  }
                }
              }
              if (q.responseType === 'number' && q.questionKey === 'sleep_duration_hours' && !q.step) {
                normalized.step = 0.5;
              }
              return normalized;
            }),
          };
        });
        setSections(sectionDefs);

        // Restore draft
        const draft = getDraft();
        if (draft && draft.answers) {
          setAnswers(draft.answers);
          setCurrentSectionIdx(Math.min(draft.currentSection, sectionDefs.length - 1));
        }
      } catch {
        setError(t('prom.error.loadFailed', 'Failed to load questions'));
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [open, t]);

  // Restore draft when reopening
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      const draft = getDraft();
      if (draft && draft.answers) {
        setAnswers(draft.answers);
        setCurrentSectionIdx(Math.min(draft.currentSection, sections.length - 1));
      }
    }
    prevOpenRef.current = open;
  }, [open, sections.length]);

  // Save draft on answer change
  const updateAnswer = useCallback(
    (questionId: string, value: unknown) => {
      let finalValue = value;

      // Logical dependencies for family_history
      if (questionId === 'family_history' && Array.isArray(value)) {
        const prevValues = answers[questionId] as number[] || [];
        const addedItems = (value as number[]).filter((v) => !prevValues.includes(v));
        if (addedItems.includes(4)) {
          finalValue = [4];
        } else if (addedItems.includes(5)) {
          finalValue = [5];
        } else if (addedItems.some((v) => v >= 0 && v <= 3)) {
          finalValue = (value as number[]).filter((v) => v !== 4 && v !== 5);
        }
      }

      // Logical dependencies for current_symptoms
      if (questionId === 'current_symptoms' && Array.isArray(value)) {
        const prevValues = answers[questionId] as number[] || [];
        const addedItems = (value as number[]).filter((v) => !prevValues.includes(v));
        if (addedItems.includes(7)) {
          finalValue = [7];
        } else if (addedItems.includes(8)) {
          finalValue = (value as number[]).filter((v) => v !== 7);
        } else if (addedItems.some((v) => v >= 0 && v <= 6)) {
          finalValue = (value as number[]).filter((v) => v !== 7);
        }
      }

      const newAnswers = { ...answers, [questionId]: finalValue };
      setAnswers(newAnswers);
      setInvalidQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
      saveDraft({ currentSection: currentSectionIdx, answers: newAnswers });

      // Save demographics to _values for downstream consumers (SubjectForm, getDefatultIndicatorValues)
      if (currentSectionIdx === 0) {
        const DEMOGRAPHICS_KEYS = new Set(['gender', 'age', 'ethnicity']);
        if (DEMOGRAPHICS_KEYS.has(questionId)) {
          updateDemographicsInStorage(questionId, value);
        }
      }
    },
    [answers, currentSectionIdx],
  );

  // Clear other symptom text when "Other" is deselected
  useEffect(() => {
    const values = answers['current_symptoms'] as number[];
    if (values && !values.includes(8)) {
      setOtherSymptomText('');
    }
  }, [answers['current_symptoms']]);

  // Clear family history "Other" text when deselected
  useEffect(() => {
    const values = answers['family_history'] as number[];
    if (values && !values.includes(6)) {
      setFamilyHistoryText('');
    }
  }, [answers['family_history']]);

  // Clear diet_type "Other" text when deselected
  useEffect(() => {
    const val = answers['diet_type'];
    if (val === undefined || val === null || val !== 6) {
      setDietTypeText('');
    }
  }, [answers['diet_type']]);

  // Clear supplement text when "No, none" is selected
  useEffect(() => {
    const val = answers['supplements'];
    if (val === undefined || val === null || val === 0) {
      setSupplementText('');
    }
  }, [answers['supplements']]);

  // Persist demographics (gender/age/ethnicity) to localStorage['_values']
  const updateDemographicsInStorage = (questionId: string, value: unknown) => {
    try {
      const existing: any = (() => {
        try {
          const hashObj = getHashObj();
          if (hashObj && Object.keys(hashObj).filter((k: string) => k).length > 0) return hashObj;
        } catch { /* ignore */ }
        try {
          const raw = localStorage.getItem('_values');
          if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        return {};
      })();

      const merged = { ...existing };
      if (questionId === 'gender') {
        merged.gender = String(value);
      } else if (questionId === 'age') {
        merged.age = Number(value);
      } else if (questionId === 'ethnicity') {
        merged.race = String(value);
      }
      localStorage.setItem('_values', JSON.stringify(merged));
    } catch {
      // silent fail — demographics live in draft/prom_draft anyway
    }
  };

  const validateCurrentSection = (): { valid: boolean; emptyFields: string[] } => {
    const section = sections[currentSectionIdx];
    if (!section) return { valid: false, emptyFields: [] };

    const emptyFields: string[] = [];
    for (const q of section.questions) {
      const ans = answers[q.id];
      if (ans === undefined || ans === null || ans === '') { emptyFields.push(q.id); continue; }
      if (Array.isArray(ans) && ans.length === 0) { emptyFields.push(q.id); continue; }
    }
    return { valid: emptyFields.length === 0, emptyFields };
  };

  const handleNext = () => {
    const result = validateCurrentSection();
    if (!result.valid) {
      setError(null);
      setInvalidQuestionIds(new Set(result.emptyFields));
      return;
    }

    setError(null);
    setInvalidQuestionIds(new Set());
    if (currentSectionIdx < sections.length - 1) {
      setCurrentSectionIdx(currentSectionIdx + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setError(null);
    setInvalidQuestionIds(new Set());
    if (currentSectionIdx > 0) {
      setCurrentSectionIdx(currentSectionIdx - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const demographics: Record<string, unknown> = {};
      const lifestyle: Record<string, unknown> = {};
      const proms: Record<string, unknown> = {};

      for (const section of sections) {
        for (const q of section.questions) {
          let val = answers[q.id];
          if (val === undefined || val === null) continue;

          if (typeof val === 'string' && !isNaN(Number(val))) {
            val = Number(val);
          }

          if (section.key === 'demographics') {
            demographics[q.id] = val;
          } else if (section.key === 'lifestyle') {
            lifestyle[q.id] = val;
          } else if (section.key === 'who5') {
            proms[q.id] = val;
          } else if (section.key === 'mood') {
            if (q.id.startsWith('phq')) {
              proms[q.id] = val;
            } else if (q.id.startsWith('gad')) {
              proms[q.id] = val;
            }
          } else if (section.key === 'energy_sleep') {
            proms[q.id] = val;
          } else if (section.key === 'body_stress') {
            if (q.id === 'stress_NRS') {
              proms[q.id] = val;
            } else if (q.id === 'abdominal_discomfort') {
              proms.abdominal_discomfort_NRS = val;
            } else {
              proms[q.id] = val;
            }
          } else if (section.key === 'digestive') {
            proms[q.id] = val;
          }
        }
      }

      setOtherSymptomText('');
      setOtherSymptomText('');
      let promsArray = Object.entries(proms).map(([key, value]) => ({ key, value }));

      if (otherSymptomText.trim()) {
        promsArray = [...promsArray, { key: 'current_symptoms_other', value: otherSymptomText.trim() }];
      }

      if (supplementText.trim()) {
        promsArray = [...promsArray, { key: 'supplements_other', value: supplementText.trim() }];
      }

      if (familyHistoryText.trim()) {
        promsArray = [...promsArray, { key: 'family_history_other', value: familyHistoryText.trim() }];
      }

      if (dietTypeText.trim()) {
        promsArray = [...promsArray, { key: 'diet_type_other', value: dietTypeText.trim() }];
      }

      const payload = { demographics, lifestyle, proms: promsArray };
      const res = await http.post('/api/prom/assessments', payload);
      const backendRes = 'data' in res ? res.data : res;
      if (backendRes && backendRes.success === false) {
        const msg = backendRes.details || backendRes.error || t('prom.error.submitFailed', 'Failed to submit assessment');
        setError(msg);
        setSubmitting(false);
        return;
      }
      const resultData = backendRes || res;
      setResult(resultData as CompletionResult);
      setCompleted(true);
      markCompleted();
      clearDraft();
      setSupplementText('');
    } catch {
      setError(t('prom.error.submitFailed', 'Failed to submit assessment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!completed) {
      saveDraft({ currentSection: currentSectionIdx, answers });
    }
    clearDraft();
    onClose();
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const section = sections[currentSectionIdx];
  const sectionPercent = sections.length > 0 ? ((currentSectionIdx + 1) / sections.length) * 100 : 0;
  const estimatedMinutes = Math.max(1, Math.round((sections.length - currentSectionIdx) * 2));

  if (loading || statusLoading) {
    return (
      <StyledDialog open={open} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </DialogContent>
      </StyledDialog>
    );
  }

  // Thank-you screen
  if (completed && result) {
    return (
      <StyledDialog open={open} maxWidth="sm" fullWidth>
        <StyledDialogTitle sx={{ pb: 0 }}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <ThankYouBox>
            <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
              {t('prom.thankYou', 'Thank you for your time')}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {result.who5Score !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  WHO-5 Score
                </Typography>
                <Typography variant="h4" color="primary">
                  {result.who5Score}
                </Typography>
              </Box>
            )}
            {result.phq2Score !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  PHQ-2 Score
                </Typography>
                <Typography variant="h4" color="primary">
                  {result.phq2Score}
                </Typography>
              </Box>
            )}
            {result.gad2Score !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  GAD-2 Score
                </Typography>
                <Typography variant="h4" color="primary">
                  {result.gad2Score}
                </Typography>
              </Box>
            )}
            {result.message && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                {getLangText(result.message, lang)}
              </Typography>
            )}
            <ReferencesSection>
              <ReferencesTitle>References</ReferencesTitle>
              <ReferenceItem>
                {REFERENCES.map((ref, i) => (
                  <li key={i}>
                    <ReferenceTitle>{ref.title}</ReferenceTitle>
                    <ul>
                    {ref.citations.map((cit, j) => {
                      if (typeof cit === 'string') {
                        return <li key={j}>{cit}</li>;
                      }
                      const parts = cit.text.split(cit.url);
                      return (
                        <li key={j}>
                          {parts[0]}
                          <ReferenceLink href={`https://${cit.url}`} target="_blank" rel="noopener noreferrer">
                            {cit.url}
                          </ReferenceLink>
                          {parts[1] || ''}
                        </li>
                      );
                    })}
                    </ul>
                  </li>
                ))}
              </ReferenceItem>
            </ReferencesSection>
          </ThankYouBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { refreshPromStatusExternally(); onClose(); }} variant="contained">
            {t('prom.close', 'Close')}
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }

  // No sections loaded
  if (!section || sections.length === 0) {
    return (
      <StyledDialog open={open} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography>{t('prom.error.noQuestions', 'No questions available')}</Typography>
        </DialogContent>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} maxWidth="md" fullWidth fullScreen={false}>
      <StyledDialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {t(`prom.sections.${section.key}.header`, getDefaultSectionLabel(section.key))}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      {/* Progress bar */}
      <ProgressContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1D9E75' }}>
              {t(`prom.sections.${section.key}.title`, getDefaultSectionLabel(section.key))}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {t(`prom.sections.${section.key}.subtitle`, '')}
            </Typography>
          </Box>
          <TimeEstimate>{estimatedMinutes} min</TimeEstimate>
        </Box>
        <ProgressBarWrapper>
          <ProgressFill sx={{ width: `${sectionPercent}%` }} />
        </ProgressBarWrapper>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {currentSectionIdx + 1} / {sections.length}
        </Typography>
      </ProgressContainer>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <QuestionnaireContainer>
              {section.questions.map((q) => (
              <QuestionBox key={q.id} invalid={invalidQuestionIds.has(q.id)}>
                <QuestionText>
                  {q.question[lang as 'en' | 'de'] || q.question.en}
                </QuestionText>

              {/* demographic pre-fill hints */}


              {/* recall hint for WHO-5 */}
              {section.key === 'who5' && q === section.questions[0] && (
                <RecallHint>
                  {t('prom.recall.overLast2Weeks', 'Over the last 2 weeks, how often have you felt...')}
                </RecallHint>
              )}

              {/* recall hint for mood */}
              {section.key === 'mood' && q === section.questions[0] && (
                <RecallHint>
                  {t('prom.recall.overLast2Weeks', 'Over the last 2 weeks, how often have you been bothered...')}
                </RecallHint>
              )}

              {/* recall hint for energy_sleep */}
              {section.key === 'energy_sleep' && q === section.questions[0] && (
                <RecallHint>
                  {t('prom.recall.overPast7Days', 'Over the past 7 days, how would you rate...')}
                </RecallHint>
              )}

              {q.responseType === 'single' && q.options && (
                <PillSelector
                  options={q.options}
                  value={answers[q.id] !== undefined ? String(answers[q.id]) : undefined}
                  onChange={(v) => updateAnswer(q.id, Number(v))}
                  lang={lang}
                  required={q.required}
                />
              )}

              {q.responseType === 'multiple' && q.options && (
                <MultipleSelector
                  options={q.options}
                  values={(answers[q.id] as number[]) || []}
                  onChange={(v) => updateAnswer(q.id, v)}
                  lang={lang}
                  required={q.required}
                />
              )}

              {q.id === 'current_symptoms' && (() => {
                const opts = q.options || [];
                const otherOpt = opts.find(o => o.label.en === 'Other');
                const values = answers[q.id] as number[] || [];
                const otherIdx = otherOpt ? opts.indexOf(otherOpt) : -1;
                const showOther = otherIdx !== -1 && values.includes(otherIdx);
                return showOther ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Please describe your symptom"
                    value={otherSymptomText}
                    onChange={(e) => setOtherSymptomText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                ) : null;
              })()}

              {q.id === 'family_history' && (() => {
                const opts = q.options || [];
                const otherOpt = opts.find(o => o.label.en === 'Other');
                const values = answers[q.id] as number[] || [];
                const otherIdx = otherOpt ? opts.indexOf(otherOpt) : -1;
                const showOther = otherIdx !== -1 && values.includes(otherIdx);
                return showOther ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Please describe your family history"
                    value={familyHistoryText}
                    onChange={(e) => setFamilyHistoryText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                ) : null;
              })()}

              {q.id === 'supplements' && (() => {
                const val = answers[q.id];
                const showOther = val !== undefined && val !== null && val !== 0;
                return showOther ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Please list the supplements you take"
                    value={supplementText}
                    onChange={(e) => setSupplementText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                ) : null;
              })()}

              {q.id === 'diet_type' && (() => {
                const opts = q.options || [];
                const otherOpt = opts.find(o => o.label.en === 'Other');
                const val = answers[q.id];
                const otherIdx = otherOpt ? opts.indexOf(otherOpt) : -1;
                const showOther = otherIdx !== -1 && val === otherIdx;
                return showOther ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Please describe your diet type"
                    value={dietTypeText}
                    onChange={(e) => setDietTypeText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                ) : null;
              })()}

              {q.responseType === 'number' && (
                <NumberInput
                  value={answers[q.id] !== undefined ? String(answers[q.id]) : ''}
                  onChange={(v) => updateAnswer(q.id, v)}
                  min={q.min}
                  max={q.max}
                  step={q.step}
                />
              )}

              {q.responseType === 'nrs' && (
                <NrsScale
                  value={answers[q.id] as number | null}
                  onChange={(v) => updateAnswer(q.id, v)}
                  scaleLabels={q.scaleLabels}
                  min={q.min}
                  max={q.max}
                  lang={lang}
                  required={q.required}
                />
              )}

              {q.responseType === 'scale' && q.scaleLabels && (
                <ScalePills
                  min={q.min ?? 0}
                  max={q.max ?? 5}
                  labels={q.scaleLabels}
                  value={answers[q.id] as number | null}
                  onChange={(v) => updateAnswer(q.id, v)}
                  lang={lang}
                  required={q.required}
                />
              )}

              {q.responseType === 'bsfs' && (
                <BsfsSelector
                  bsfsLabels={q.bsfsLabels}
                  value={answers[q.id] as number | null}
                  onChange={(v) => updateAnswer(q.id, v)}
                  lang={lang}
                />
              )}
            </QuestionBox>
          ))}

          {/* body_stress stress definition frame */}
          {section.key === 'body_stress' && (
            <Box sx={{ my: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" color="text.secondary">
                {t('prom.stressDefinition', STRESS_DEFINITION[lang as 'en' | 'de'] || STRESS_DEFINITION.en)}
              </Typography>
            </Box>
          )}
        </QuestionnaireContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleBack}
          disabled={currentSectionIdx === 0}
          sx={{ mr: 'auto' }}
        >
          {t('prom.back', 'Back')}
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={submitting}
          sx={{ minWidth: 100 }}
        >
          {submitting ? <CircularProgress size={24} /> : currentSectionIdx === sections.length - 1
            ? t('prom.submit', 'Submit')
            : t('prom.next', 'Next')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
